// src/components/treinamentos/TreinamentoMarkdownRenderer.js
import React from "react";
import { AlertTriangle, Info, CheckCircle2, ChevronRight, Terminal, Copy, Check } from "lucide-react";

/**
 * Parser e Renderizador de Markdown Customizado para os Treinamentos da Sempre Joias.
 * Converte headers (###, ####), negrito (**), itálico (*), código inline (`),
 * blocos de código (```text), listas, tabelas e callouts (>) em componentes JSX com Tailwind.
 */
export default function TreinamentoMarkdownRenderer({ content }) {
  if (!content) return null;

  // Quebra o conteúdo em linhas
  const lines = content.split(/\r?\n/);
  const elements = [];
  let i = 0;

  // Helper para formatar texto inline (negrito, código, itálico)
  const formatInline = (text, keyPrefix = "") => {
    if (!text) return "";

    // Expressão regular para tokens inline:
    // 1. `código inline`
    // 2. **negrito**
    // 3. *itálico*
    const tokens = [];
    let remaining = text;
    let tokenIndex = 0;

    while (remaining.length > 0) {
      // 1. Código inline `code`
      const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/s);
      // 2. Negrito **bold**
      const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*(.*)$/s);

      // Prioriza o que vier primeiro
      const codeIndex = remaining.indexOf("`");
      const boldIndex = remaining.indexOf("**");

      if (codeIndex !== -1 && (boldIndex === -1 || codeIndex < boldIndex)) {
        const pre = remaining.substring(0, codeIndex);
        const end = remaining.indexOf("`", codeIndex + 1);
        if (end !== -1) {
          if (pre) tokens.push(formatInlinePure(pre, `${keyPrefix}-pre-${tokenIndex++}`));
          const codeVal = remaining.substring(codeIndex + 1, end);
          tokens.push(
            <code
              key={`${keyPrefix}-code-${tokenIndex++}`}
              className="px-1.5 py-0.5 mx-0.5 rounded-md bg-slate-100 border border-slate-200 font-mono text-indigo-700 text-xs font-bold"
            >
              {codeVal}
            </code>
          );
          remaining = remaining.substring(end + 1);
          continue;
        }
      }

      if (boldIndex !== -1) {
        const pre = remaining.substring(0, boldIndex);
        const end = remaining.indexOf("**", boldIndex + 2);
        if (end !== -1) {
          if (pre) tokens.push(formatInlinePure(pre, `${keyPrefix}-pre-${tokenIndex++}`));
          const boldVal = remaining.substring(boldIndex + 2, end);
          tokens.push(
            <strong key={`${keyPrefix}-bold-${tokenIndex++}`} className="font-bold text-slate-950">
              {boldVal}
            </strong>
          );
          remaining = remaining.substring(end + 2);
          continue;
        }
      }

      // Se não há mais matches, adiciona o restante
      tokens.push(formatInlinePure(remaining, `${keyPrefix}-rest-${tokenIndex++}`));
      break;
    }

    return tokens;
  };

  const formatInlinePure = (str, key) => {
    return <span key={key}>{str}</span>;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // 1. Linha vazia
    if (!trimmed) {
      i++;
      continue;
    }

    // 2. Separador Horizontal (---)
    if (/^---+$/.test(trimmed)) {
      elements.push(<hr key={`hr-${i}`} className="my-6 border-slate-200" />);
      i++;
      continue;
    }

    // 3. Cabeçalhos (### ou #### ou ##)
    if (trimmed.startsWith("### ")) {
      const title = trimmed.replace(/^###\s+/, "");
      elements.push(
        <h3 key={`h3-${i}`} className="text-base sm:text-lg font-bold text-slate-900 mt-6 mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-indigo-600" />
          <span>{formatInline(title, `h3-${i}`)}</span>
        </h3>
      );
      i++;
      continue;
    }

    if (trimmed.startsWith("#### ")) {
      const title = trimmed.replace(/^####\s+/, "");
      elements.push(
        <h4 key={`h4-${i}`} className="text-sm sm:text-base font-bold text-slate-800 mt-4 mb-2 uppercase tracking-wide">
          {formatInline(title, `h4-${i}`)}
        </h4>
      );
      i++;
      continue;
    }

    if (trimmed.startsWith("## ")) {
      const title = trimmed.replace(/^##\s+/, "");
      elements.push(
        <h2 key={`h2-${i}`} className="text-lg sm:text-xl font-bold text-indigo-950 mt-8 mb-3 pb-1 border-b border-indigo-100">
          {formatInline(title, `h2-${i}`)}
        </h2>
      );
      i++;
      continue;
    }

    // 4. Bloco de Código / Exemplo (```text ou ```)
    if (trimmed.startsWith("```")) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // pula o fechamento ```

      const codeContent = codeLines.join("\n");
      elements.push(
        <div key={`codeblock-${i}`} className="my-4 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 text-slate-100 shadow-sm">
          <div className="px-3.5 py-1.5 bg-slate-950 border-b border-slate-800 flex items-center justify-between text-[11px] text-slate-400 font-mono">
            <span className="flex items-center gap-1.5">
              <Terminal size={12} className="text-indigo-400" />
              <span>Exemplo / Roteiro Prático</span>
            </span>
          </div>
          <pre className="p-4 text-xs font-mono whitespace-pre-wrap leading-relaxed overflow-x-auto text-emerald-300">
            {codeContent}
          </pre>
        </div>
      );
      continue;
    }

    // 5. Citação / Callout (> ...)
    if (trimmed.startsWith("> ")) {
      const quoteText = trimmed.replace(/^>\s*/, "");
      const isWarning = quoteText.includes("⚠️") || quoteText.toLowerCase().includes("atenção");
      elements.push(
        <div
          key={`quote-${i}`}
          className={`my-3 p-3.5 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            isWarning
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-indigo-50/80 border-indigo-200 text-indigo-950"
          }`}
        >
          {isWarning ? (
            <AlertTriangle size={17} className="text-amber-600 shrink-0 mt-0.5" />
          ) : (
            <Info size={17} className="text-indigo-600 shrink-0 mt-0.5" />
          )}
          <div className="leading-relaxed">{formatInline(quoteText, `quote-${i}`)}</div>
        </div>
      );
      i++;
      continue;
    }

    // 6. Tabela Markdown (| Col 1 | Col 2 | ...)
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|") && lines[i].trim().endsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        const headerCols = tableLines[0]
          .split("|")
          .filter((c, idx, arr) => idx > 0 && idx < arr.length - 1)
          .map((c) => c.trim());

        // Pula linha de separação (|---|---|)
        const rowLines = tableLines.slice(2);

        elements.push(
          <div key={`table-${i}`} className="my-4 overflow-x-auto rounded-xl border border-slate-200 shadow-2xs">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-800 font-bold border-b border-slate-200">
                  {headerCols.map((col, cIdx) => (
                    <th key={cIdx} className="py-2.5 px-3 whitespace-nowrap">
                      {formatInline(col, `th-${cIdx}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rowLines.map((rLine, rIdx) => {
                  const cols = rLine
                    .split("|")
                    .filter((c, idx, arr) => idx > 0 && idx < arr.length - 1)
                    .map((c) => c.trim());

                  return (
                    <tr key={rIdx} className="hover:bg-slate-50 transition">
                      {cols.map((col, cIdx) => (
                        <td key={cIdx} className="py-2 px-3 text-slate-700 leading-relaxed align-top">
                          {formatInline(col, `td-${rIdx}-${cIdx}`)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // 7. Lista não-ordenada (- item ou * item)
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const listItems = [];
      while (i < lines.length && (lines[i].trim().startsWith("- ") || lines[i].trim().startsWith("* "))) {
        listItems.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }

      elements.push(
        <ul key={`ul-${i}`} className="my-2 space-y-1.5 pl-2">
          {listItems.map((item, lIdx) => (
            <li key={lIdx} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-2" />
              <div>{formatInline(item, `li-${lIdx}`)}</div>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // 8. Lista ordenada (1. item, 2. item)
    if (/^\d+\.\s+/.test(trimmed)) {
      const listItems = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s+/, "");
        listItems.push(itemText);
        i++;
      }

      elements.push(
        <ol key={`ol-${i}`} className="my-2 space-y-1.5 pl-2">
          {listItems.map((item, lIdx) => (
            <li key={lIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-800 leading-relaxed">
              <span className="w-5 h-5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-bold font-mono flex items-center justify-center shrink-0 mt-0.5">
                {lIdx + 1}
              </span>
              <div>{formatInline(item, `oli-${lIdx}`)}</div>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // 9. Parágrafo comum
    elements.push(
      <p key={`p-${i}`} className="text-xs sm:text-sm text-slate-700 leading-relaxed my-2">
        {formatInline(trimmed, `p-${i}`)}
      </p>
    );
    i++;
  }

  return <div className="space-y-1">{elements}</div>;
}
